import { SELECTION_COLORS, SELECTION_REASONS } from './atlasSelectionUtils';
import { SelectionElement, SelectionObject } from './core-elements/BasicElements.js';
import markerUtils from './markerUtils';

let viewer = null;
let PLMVisWeb = null;
/** @type {HTMLElement} */
let svgOverlay = null;
let markRadius = 1;
let sgoMgr = null;
let Sphere = null;

let camChange = false;
/**
 *
 * @param {object} newCamInfo
 * @param {any} action
 */
const onCameraChanged = (newCamInfo, action) => {
    if (action === PLMVisWeb.CameraChange.PRE) {
        camChange = true;
        markerUtils.clearMarkers(svgOverlay);
    } else {
        camChange = false;
        markerUtils.updateMarkers(PLMVisWeb, viewer, svgOverlay, markRadius);
    }
};

/**
 *
 * @param {object} viewer - An instance of PLMVisWeb viewer
 * @param {SnapPointSelectionObject} selectionObject
 * @returns {number[]}
 */
const getPointLocationInAssembly = (viewer, selectionObject) => {
    let worldPt = new PLMVisWeb.THREE.Vector3();
    let worldMatrix = new PLMVisWeb.THREE.Matrix4();
    if (selectionObject.result.isPrototypeSpace) {
        const worldMatrixArray = viewer.getWorldMatrixByPsId(selectionObject.result.parentPsId);
        worldMatrix.fromArray(worldMatrixArray);
    }
    worldPt.copy(selectionObject.result.location).applyMatrix4(worldMatrix);

    return [worldPt.x, worldPt.y, worldPt.z];
};

/**
 *
 * @param {object} viewer - An instance of PLMVisWeb viewer
 * @param {object} sgoMgr
 * @param {SnapPointSelectionObject} selectionObject
 */
const createPointSGO = (viewer, sgoMgr, selectionObject) => {
    // Sometimes, when the geometry is modeled away such as the case in PR10815473, the client can still
    // select the geometry. The geometry is there at the time of selection, but when the modeling operation
    // finishes, the geometry is modeled away. In this case, when the command finishes, it needs to clean
    // up the selected and it fails when encountering the modeled away geometry.
    // We should filter out these kinds of geometry - don't do any thing
    if (viewer.getObjectTypeByPsId(selectionObject.result.parentPsId) === '') {
        return;
    }
    let pntLoc = getPointLocationInAssembly(viewer, selectionObject);
    let pointSGO = new Sphere(viewer, pntLoc, 'pointSGO', SELECTION_COLORS, null, false);
    // Need to transfer data from marker to sgo
    pointSGO.SPLM_PsId = selectionObject.marker ? selectionObject.marker.SPLM_PsId : undefined;
    pointSGO.SPLM_Loc = selectionObject.marker ? selectionObject.marker.SPLM_Loc : undefined;
    if (pointSGO.SPLM_Loc) {
        pointSGO.SPLM_Loc.marker = undefined; // tidy up
    }
    sgoMgr.addSGO(pointSGO);
    selectionObject.sgo = pointSGO;
    selectionObject.result.sgo = pointSGO;
    pointSGO.selectionObject = selectionObject;
};

/**
 * Selction Object for SnapPoint
 */
export class SnapPointSelectionObject extends SelectionObject {
    /**
     * @param {Array | string} id - In general, id is the atlasId(Array) of object but some SelectionElement use string as id
     * @param {string[]} psIds - psIds of geometries that represent this selected object
     * @param {string} type - type of this selection object
     * @param {string} displayName - display name(localization is not supported yet?)
     * @param {SelectionElement} sourceElement - might be undefined!
     */
    constructor(id, psIds, type, displayName, sourceElement) {
        super(id, psIds, type, displayName, sourceElement);
        /** @type {import('./markerUtils').SVGElementExt} */
        this.marker = undefined;
        /** @type {object} */
        this.sgo = undefined;
        /** @type {import('./atlasSelectionUtils').LocData} */
        this.result = undefined;
        /** @type {boolean} */
        this.cancelAccepted = undefined;
    }
}

/**
 * SnapPointBaseElement constructor.  This element allows snap point selection.  It behaves like any
 * other SelectionElement in that it generates selection objects that are added onto the selection list.  These
 * selection objects contain the precise point xyz in the result member, and the SDK objects necessary for
 * Atlas to generate the selected point in the recipe member.
 */
export class SnapPointBaseElement extends SelectionElement {
    /**
     * @param {object}   controlManagerInp - The PLMVisWeb control manager.  It is used to access the viewer
     *                                       dimensions, and to gain access to the Viewer object in use.
     * @param {object}   PLMVisWebInp      - The PLMVisWeb module itself
     * @param {HTMLElement} svgOverlayInp  - The SVG DOM element that overlays the PLMVisWeb canvas.  This element
     *                                       must be supplied by the application.  Galaxy already provides and maintains
     *                                       this element. Due to performance considerations, this element uses SVG for
     *                                       prehighlighing of the snap point locations.
     * @param {object}   SPLMInp           - The SPLM SDK object, which must have the point construction APIs defined.
     * @param {object}   SphereSGO         - An object that provides sphere (sgo) constructor
     * @param {Function} sendApiInp        - A function that will send an API message to Atlas asynchronously. This function is
     *                                       expected to take one input object { API: xxx, ApiDescription: yyy }:
     *                                            1) xxx: the array of SPLM SDK instructions (SPLM.api),
     *                                            2) yyy: the description for the API which is used as API message Id.
     *                                       It should return a promise that resolves with the api result from Atlas.
     *                                       Galaxy already provides this function.
     * @param {import('./AtlasSelection.js').AtlasSelection} atlasSelInp
     */
    constructor(controlManagerInp, PLMVisWebInp, svgOverlayInp, SPLMInp, SphereSGO, sendApiInp, atlasSelInp) {
        super(controlManagerInp.viewer);
        PLMVisWeb = PLMVisWebInp;
        if (viewer !== controlManagerInp.viewer) {
            viewer = controlManagerInp.viewer;
            viewer.registerCameraChangedEvent(onCameraChanged, PLMVisWeb.CameraChange.BOTH);
        }
        sgoMgr = viewer.sgo;
        this._sgoMgr = sgoMgr;
        svgOverlay = svgOverlayInp;
        this._markRadius = 0.003 * markerUtils.getViewDiagonal(svgOverlay);
        markRadius = this._markRadius;
        Sphere = SphereSGO;
    }
    get sgoMgr() {
        return this._sgoMgr;
    }
    /**
     * @inheritdoc
     * @param {SnapPointSelectionObject} selectionObject
     * @param {boolean} select
     * @param {SELECTION_REASONS} reason
     */
    select(selectionObject, select, reason) {
        //need to display permanant marker here...
        // don't return edge psId, otherwise AtlasSelection will ask PLMVW to select it
        if (select && !selectionObject.marker) {
            markerUtils.displayMarker(PLMVisWeb, viewer, svgOverlay, markRadius, selectionObject.psIds[0], selectionObject.result);
            selectionObject.marker = selectionObject.result.marker;
        }
        if (select) {
            if (selectionObject.result.sgo) {
                if (reason === SELECTION_REASONS.FOCUS_OUT) {
                    selectionObject.result.sgo.hide();
                } else {
                    selectionObject.result.sgo.show();
                }
            } else {
                createPointSGO(viewer, sgoMgr, selectionObject);
            }
        } else {
            if (selectionObject.result.sgo) {
                if (reason === SELECTION_REASONS.TERMINATE) {
                    sgoMgr.removeSGO(selectionObject.result.sgo);
                    selectionObject.result.sgo = undefined;
                    selectionObject.sgo = undefined;
                } else if (reason === SELECTION_REASONS.FOCUS_OUT) {
                    selectionObject.result.sgo.hideLocate();
                    selectionObject.result.sgo.showSecondary();
                } else {
                    selectionObject.result.sgo.hide();
                }
            }
        }
        markerUtils.onSelect(svgOverlay, selectionObject, select);
        return [];
    }

    onPop() {
        markerUtils.clearMarkers(svgOverlay);
    }

    /**
     * Snap point need to update sgo when added to selection list
     * @param {import('./Context.js').Context} context
     * @param {SnapPointSelectionObject} selectionObject
     */
    onAddToSelected(context, selectionObject) {
        if (context.getSecondaryActive()) {
            this.updateSgo(selectionObject);
        }
    }

    /**
     * @param {import('./Context.js').Context} context
     * @param {SnapPointSelectionObject} selectionObject
     */
    onRemoveFromSelected(context, selectionObject) {
        this.removeSgo(selectionObject);
    }

    /**
     * SecondaryHighlight selectionObject. SelectionElements can have
     *    unique selection behavior, see PmiElement for example
     *
     * @param {SnapPointSelectionObject} selectionObject - Object to select
     * @param {Boolean} secondaryHighlight - true: add secondary Highlight color to object
     *                           false:  cancel secondary highlight on object
     * @returns {Array} Returns array of psIds to secondaryHighlight in PLMVisWeb, which will
     *                  highlight the psIds in the secondaryHighlight color.
     */
    secondaryHighlight(selectionObject, secondaryHighlight) {
        // In some special cases, for example:
        //     a) selection component inherits the globally selected snap point and,
        //     b) the selection component is set to focus out.
        // In this case, the selection component is replaced by another. In this process,
        //     a) we pop the current selection component containing the snap point;
        //     b) we deselected the snap points in the global context before the new context is replaced, then,
        //     c) add the new context to the context list.
        // Because of this, the inherited snap point is without SGO. Before drawing the secondary selection color to
        // this green object, we need to update its SGO. updateSgo will do nothing if snap point has sgo.
        this.updateSgo(selectionObject);
        super.secondaryHighlight(selectionObject, secondaryHighlight);

        return [];
    }

    /**
     * Use case: when a globally selected snap point is inherited by a selection component, the globally selected
     * snap point in the global selection context is deselected. This causes the SGO in the snap point to be removed.
     * Then the snap point is added into the selection component without a SGO. We need to update the SGO to the
     * selected snap point and then add it to the new selection component. updateSGO is for this purpose.
     *
     * @param {SnapPointSelectionObject} selectionObject
     */
    updateSgo(selectionObject) {
        if (selectionObject.result && selectionObject.result.sgo === undefined && selectionObject.result.location) {
            createPointSGO(viewer, sgoMgr, selectionObject);
            // leaving set sgo color for the application
        }
    }

    /**
     * @param {SnapPointSelectionObject} selectionObject
     */
    removeSgo(selectionObject) {
        if (selectionObject.result && selectionObject.result.sgo) {
            sgoMgr.removeSGO(selectionObject.result.sgo);
            selectionObject.result.sgo = undefined;
            selectionObject.sgo = undefined;
        }
    }
}

export default SnapPointSelectionObject;
